import { istBeglaubigung, istVorschaubild } from '../../domain/abgeleitet';
import { artAusPfad, keineDateien, type Dateiart } from '../../domain/rueckstand';
import { listeOrdner } from '../../platform/dateien';
import {
  istAufgegeben,
  istFaellig,
  merkeErfolg,
  merkeFehlschlag,
  staende,
} from '../../persistence/anhaenge';
import type { Store } from '../../persistence/port';
import { appendAppLog } from '../../platform/appLog';
import { geraeteName } from '../../platform/appVersion';
import { listFiles, readBase64, type LogKind, type LogScope } from '../../platform/storage';
import { felderAusName } from '../multipart';
import { ladeDiagnoseHoch } from './diagnose';
import { classicSender, ladeHoch, type HochladenDeps, type HochzuladendeDatei, type MultipartSender } from './hochladen';
import type { ClassicClientRequest } from './typen';

/**
 * Was die App tut, wenn der Prüfungsleiter Dateien anfordert.
 *
 * Der Server schickt die Anforderung im Herzschlag mit (`clientRequests` in der Antwort auf
 * `POST /rest/log`, s. `senden.ts`) und **verwirft sie danach**. Sie wird deshalb sofort im
 * selben Durchgang erfüllt, nicht auf später gelegt.
 *
 * **Welche Datei zu welcher Anforderung gehört** (Entscheidung des Nutzers, 2026-09-13): Der
 * Classic-Server kennt genau zwei Anforderungsnamen. `UPLOAD_SCREENSHOTS` nimmt deshalb **alles
 * Aufgenommene** — Abzüge, Fotos, Ton, Video, Handschrift —, `UPLOAD_LOGS` die
 * Tagesprotokolle. Der Classic-Client lädt ohnehin jede Art über denselben Endpunkt hoch; die
 * Liste der Medientypen, die er bildet, führt Bilder, Ton und Video nebeneinander.
 *
 * ⚠️ **Das ist die Grenze des Classic-Servers, nicht unsere.** Im Relay-Modus wird feiner
 * aufgeteilt — je Medienart ein eigener Weg. Wer die zwei Namen hier in den gemeinsamen Teil
 * zöge, verkrüppelte Relay an einem Protokoll, das abgelöst werden soll.
 */

/** Alle Bezüge, in denen Dateien liegen können (`platform/storage.ts`). */
const BEZUEGE: LogScope[] = ['station', 'station-comment', 'task-comment'];

/**
 * Was auf `UPLOAD_SCREENSHOTS` hinausgeht: jede Art, die der Prüfer aufnimmt. Das ist
 * absichtlich `LogKind` vollständig — kommt eine Art dazu, fährt sie von selbst mit.
 */
const BEI_AUFNAHMEN: LogKind[] = ['screenshot', 'photo', 'audio', 'video', 'handwriting'];

/*
 * **Was von den abgeleiteten Dateien hier bleibt** (`domain/abgeleitet.ts`):
 *
 * - Die **Beglaubigung** `.sig`. Der Classic-Server kann eine Signatur nicht deuten; sie landete als
 *   Fremdkörper zwischen den Abzügen und wäre in der Auswertung nur eine Zeile Rauschen. Im
 *   Relay-Modus gehört sie dagegen zum Beleg und fährt mit (Entscheidung des Nutzers, 2026-09-13).
 * - Das **Vorschaubild** `-thumb.jpg`. Es ist abgeleitet, nicht aufgenommen — ein Einzelbild des
 *   Videos, damit die Liste am Gerät etwas zu zeigen hat. Der Classic-Client kennt es gar nicht; in
 *   der Auswertung stünde neben jedem Video ein zweiter Abzug, den niemand angefertigt hat.
 *
 * ⚠️ **Die Markierebene `-overlay.svg` geht mit.** Sie ist Inhalt, kein Beweis: ohne sie fehlt
 * auf dem Server, was der Prüfer ins Foto gezeichnet hat. Ihre Felder liest
 * `hochladen.ts::baueFelder` über `felderAusName`, das die Endung selbst abschneidet.
 */

/** Der Ordner der Tagesprotokolle — er hängt an keinem Prüfungsbezug. */
const APP_LOG_ORDNER = 'logs/app';

/** Die Dateien, die zu einer Anforderung gehören — ohne Rücksicht auf den Übertragungsstand. */
export async function sammleDateien(art: ClassicClientRequest): Promise<HochzuladendeDatei[]> {
  if (art === 'UPLOAD_SCREENSHOTS') {
    const gefunden: HochzuladendeDatei[] = [];
    for (const bezug of BEZUEGE) {
      for (const sorte of BEI_AUFNAHMEN) {
        for (const pfad of await listFiles(bezug, sorte)) {
          if (istBeglaubigung(pfad) || istVorschaubild(pfad)) continue;
          gefunden.push({ pfad, art: sorte });
        }
      }
    }
    return gefunden;
  }

  // UPLOAD_LOGS: die Tagesprotokolle. Sie liegen nicht unter einem Bezug, deshalb hier von
  // Hand statt über `listFiles`.
  try {
    const { dateien } = await listeOrdner(APP_LOG_ORDNER);
    return dateien.map((name) => ({ pfad: APP_LOG_ORDNER + '/' + name, art: 'log' as const }));
  } catch {
    // Kein Ordner heisst: noch nichts mitgeschrieben. Kein Fehler.
    return [];
  }
}

export interface AnforderungsDeps {
  zugang: { baseUrl: string; login: string; password: string };
  deviceID: string;
  deviceName?: string;
  /** Einschiebbar für Tests. */
  sammle?: (art: ClassicClientRequest) => Promise<HochzuladendeDatei[]>;
  lese?: (pfad: string) => Promise<string>;
  senden?: MultipartSender;
  log?: (zeile: string) => Promise<void>;
  /**
   * Der Diagnose-Upload auf `UPLOAD_LOGS` (`diagnose.ts`). `null` schaltet ihn ab — dann gehen
   * die Protokolle wie früher einzeln über den Datei-Upload (für Tests des alten Wegs).
   */
  diagnose?: (() => Promise<unknown>) | null;
  /** Formatwandlung vor dem Senden (`platform/classicWandel.ts`); fehlt sie, geht alles roh. */
  wandle?: HochladenDeps['wandle'];
  /**
   * Die Ablage, in der der Übertragungsstand je Datei steht. Fehlt sie, wird nichts vermerkt —
   * das ist der Fall in Tests, die nur den Transport prüfen.
   */
  store?: Store;
}

/**
 * Die Buchführung je Datei, angebunden an die Ablage.
 *
 * ⚠️ **Dieselbe Tabelle wie im Relay-Weg** (`persistence/anhaenge.ts`): ein Gerät fährt ein
 * Gleis, und zwei Tabellen für dieselbe Frage liefen irgendwann auseinander. Die `examID` ist
 * darin nur eine Notiz; sie kommt aus dem Dateinamen, nicht aus dem laufenden Betrieb.
 */
function buchfuehrung(store: Store | undefined) {
  if (!store) return undefined;
  const examIdVon = (pfad: string) =>
    felderAusName(pfad.slice(pfad.lastIndexOf('/') + 1)).examID ?? null;
  return {
    erfolg: (datei: HochzuladendeDatei) =>
      merkeErfolg(store, { pfad: datei.pfad, examID: examIdVon(datei.pfad), art: datei.art }),
    fehlschlag: (datei: HochzuladendeDatei, fehler: string, abgewiesen: boolean) =>
      merkeFehlschlag(store, {
        pfad: datei.pfad,
        examID: examIdVon(datei.pfad),
        art: datei.art,
        fehler,
        abgewiesen,
      }),
  };
}

/**
 * Was noch nicht durch ist — die Grundlage für das selbsttätige Nachschieben **und** für die
 * Zahl in der Kopfzeile.
 *
 * ⚠️ **`nurFaellige` trennt die zwei Anlässe.** Schiebt der Client von selbst nach, gilt der
 * Rückzug: eine Datei, die gerade abgelehnt wurde, wartet ihre Frist ab. Fordert der
 * Prüfungsleiter dagegen an, will er sie **jetzt** — dann zählt nur, ob sie schon durch ist.
 */
export async function offeneDateien(
  store: Store,
  optionen: { nurFaellige?: boolean; jetzt?: Date } = {},
): Promise<HochzuladendeDatei[]> {
  const jetzt = optionen.jetzt ?? new Date();
  const stand = await staende(store);
  return (await sammleDateien('UPLOAD_SCREENSHOTS')).filter((datei) => {
    const zeile = stand.get(datei.pfad);
    if (zeile?.erledigt === 'Y') return false;
    if (istAufgegeben(zeile)) return false;
    return optionen.nurFaellige ? istFaellig(zeile, jetzt) : true;
  });
}

/**
 * Wie viele Dateien noch ausstehen und wie viele aufgegeben sind — für die Rückstandspille.
 *
 * Aufgegebene stehen eigens: sie warten auf nichts mehr. Die Datei liegt weiter auf dem iPad,
 * nur der Weg zum Server ist zu.
 */
export async function classicDateistand(
  store: Store,
): Promise<{ offen: number; abgewiesen: number; jeArt: Record<Dateiart, number> }> {
  const stand = await staende(store);
  let offen = 0;
  let abgewiesen = 0;
  const jeArt = keineDateien();
  for (const datei of await sammleDateien('UPLOAD_SCREENSHOTS')) {
    const zeile = stand.get(datei.pfad);
    if (zeile?.erledigt === 'Y') continue;
    if (istAufgegeben(zeile)) abgewiesen += 1;
    else {
      offen += 1;
      jeArt[artAusPfad(datei.pfad)] += 1;
    }
  }
  return { offen, abgewiesen, jeArt };
}

/**
 * Die Anforderungen eines Durchgangs erfüllen.
 *
 * ⚠️ **Jede Art wird ein eigener Stapel.** `progressTick` und `progressFinished` beziehen sich
 * auf einen Stapel; würden Abzüge und Protokolle in einen geworfen, meldete das Gerät der
 * Konsole „fertig" für etwas, das der Prüfungsleiter so nie angefordert hat.
 */
export function erfuelleAnforderungen(deps: AnforderungsDeps) {
  const sammle = deps.sammle ?? sammleDateien;
  const lese = deps.lese ?? readBase64;
  const log = deps.log ?? appendAppLog;
  // Welcher Weg: nativ auf dem Gerät, `fetch` im Browser — die Wahl trifft `server/multipart.ts`.
  const senden = deps.senden ?? classicSender(deps.zugang);

  const merke = buchfuehrung(deps.store);

  return async (arten: ClassicClientRequest[]): Promise<void> => {
    for (const art of arten) {
      // Das Diagnose-ZIP hat seinen eigenen Weg — ein rohes ZIP, kein Datei-Upload (2026-10-09).
      if (art === 'UPLOAD_LOGS' && deps.diagnose !== null) {
        await (deps.diagnose ?? (() => ladeDiagnoseHoch({ zugang: deps.zugang, deviceID: deps.deviceID, store: deps.store, log })))();
        continue;
      }
      /*
       * ⚠️ **Auch eine Anforderung überspringt, was schon durch ist** (seit 2026-09-22). Bis
       * dahin lud jede Anforderung alles erneut hoch — bei dreihundert Aufnahmen eines
       * Prüfungstages jedes Mal von vorn. Der Rückzug gilt hier aber nicht: fordert der
       * Prüfungsleiter an, will er die Dateien jetzt.
       */
      const dateien =
        art === 'UPLOAD_SCREENSHOTS' && deps.store
          ? await offeneDateien(deps.store)
          : await sammle(art);
      if (dateien.length === 0) {
        await log('Anforderung ' + art + ': nichts offen.');
        continue;
      }
      await ladeHoch({
        basisUrl: deps.zugang.baseUrl,
        dateien,
        leseBase64: lese,
        deviceName: deps.deviceName ?? geraeteName(),
        deviceID: deps.deviceID,
        senden,
        log,
        wandle: deps.wandle,
        // Tagesprotokolle bekommen keinen Vermerk: sie wachsen weiter, und „erledigt" wäre
        // eine Aussage über eine Datei, die es morgen so nicht mehr gibt.
        merke: art === 'UPLOAD_SCREENSHOTS' ? merke : undefined,
      });
    }
  };
}

/**
 * Was der Client von sich aus nachschiebt — ohne dass der Prüfungsleiter etwas anfordert.
 *
 * **Vorgabe des Nutzers, 2026-09-22:** im Classic-Modus gehen Aufnahmen hinaus, sobald sie
 * anfallen, und zwar so lange, bis sie durch sind. Vorher hing alles an `clientRequests` — ein
 * Foto konnte einen ganzen Prüfungstag auf dem iPad liegen bleiben, ohne dass jemand es merkte.
 *
 * ⚠️ **Nur Aufnahmen, keine Tagesprotokolle.** Die wachsen bei jedem Logeintrag weiter; sie von
 * selbst zu schicken hiesse, dieselbe Datei im Minutentakt erneut hochzuladen. Sie bleiben an
 * der Anforderung.
 */
export function sendeOffeneDateien(deps: AnforderungsDeps & { store: Store }) {
  const lese = deps.lese ?? readBase64;
  const log = deps.log ?? appendAppLog;
  const senden = deps.senden ?? classicSender(deps.zugang);
  const merke = buchfuehrung(deps.store);

  return async (): Promise<{ gesendet: number; gescheitert: number }> => {
    const dateien = await offeneDateien(deps.store, { nurFaellige: true });
    if (dateien.length === 0) return { gesendet: 0, gescheitert: 0 };
    return ladeHoch({
      basisUrl: deps.zugang.baseUrl,
      dateien,
      leseBase64: lese,
      deviceName: deps.deviceName ?? geraeteName(),
      deviceID: deps.deviceID,
      senden,
      log,
      wandle: deps.wandle,
      merke,
    });
  };
}
